import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Upload as KendoUpload } from "@progress/kendo-react-upload";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import Papa from "papaparse";

import ErrorBanner from "../../components/ErrorBanner";
import {
  useQueryGetPrismInternalUsers,
  useMutatePostSalesAdminMapping,
} from "../../data/services/salesAdminMappingService";
import { normalizeUserId as normalize } from "../../utils/salesAdminUserIds";

export const ACTIONS = {
  ADD: "Add",
  ALREADY_MAPPED: "Already Mapped",
  REACTIVATE: "Reactivate",
  INVALID: "Invalid",
  MAPPED_ELSEWHERE: "Mapped Elsewhere",
};

const TRUTHY_ALLOW_ALL = new Set(["1", "true", "yes"]);
const FALSY_ALLOW_ALL = new Set(["", "0", "false", "no"]);

// Blank reads as false. Anything outside the recognized true/false spellings
// returns undefined rather than quietly defaulting to false: when
// BdmUsername is also populated, silently treating a typo'd AllowAllClients
// as false would import the row as an ordinary single-BDM mapping and drop
// the admin's intended allow-all with zero warning - a silent visibility
// regression, not a formatting nicety. Failing loudly here matches how the
// file already treats unknown headers and contradictory values.
const parseAllowAllClients = (raw) => {
  const normalized = normalize(raw);
  if (TRUTHY_ALLOW_ALL.has(normalized)) return true;
  if (FALSY_ALLOW_ALL.has(normalized)) return false;
  return undefined;
};

/**
 * CSV import for Sales Admin -> BDM mappings.
 *
 * Upsert-only: mappings already active are left unchanged, and mappings
 * absent from the file are never removed. Removing a mapping restores broad
 * client visibility to that Sales Admin, so it must stay an explicit action
 * on the grid, never a side effect of uploading a partial file.
 *
 * The save command (`useMutatePostSalesAdminMapping`) is per-Sales-Admin, so
 * rows are grouped by Sales Admin and one mutation is issued per group; the
 * returned outcomes are aggregated across groups for display.
 */
const SalesAdminMappingImportModal = ({
  currentMappings,
  onClose,
  onSubmit,
}) => {
  const [files, setFiles] = React.useState([]);
  const [parseError, setParseError] = React.useState(null);
  const [fileRows, setFileRows] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [results, setResults] = React.useState(null);

  const { data: usersResponse } = useQueryGetPrismInternalUsers();
  const saveHandler = useMutatePostSalesAdminMapping();

  const users = React.useMemo(() => usersResponse?.data ?? [], [usersResponse]);

  const usersByNormalizedName = React.useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(normalize(u.userName), u));
    return map;
  }, [users]);

  const usersByNormalizedId = React.useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(normalize(u.userId), u));
    return map;
  }, [users]);

  // Two lookups built from the current (pre-import) state:
  //  - mappingsByPair: is this exact Sales Admin/BDM pair already stored, and
  //    is it active (Already Mapped) or soft-deleted (Reactivate)?
  //  - activeSalesAdminByBdm: which Sales Admin (if any) actively holds this
  //    BDM today, so a different Sales Admin claiming it previews as Mapped
  //    Elsewhere rather than Add.
  const { mappingsByPair, activeSalesAdminByBdm } = React.useMemo(() => {
    const pairs = new Map();
    const activeByBdm = new Map();

    (currentMappings || []).forEach((mapping) => {
      const saNorm = normalize(mapping.salesAdminUserId);
      const bdmNorm = mapping.bdmUserId ? normalize(mapping.bdmUserId) : "";
      const active = mapping.active !== false;

      pairs.set(`${saNorm}||${bdmNorm}`, { active });
      if (active && bdmNorm) {
        activeByBdm.set(bdmNorm, saNorm);
      }
    });

    return { mappingsByPair: pairs, activeSalesAdminByBdm: activeByBdm };
  }, [currentMappings]);

  const previewRows = React.useMemo(() => {
    return fileRows.map((row, index) => {
      // The grid shows the user id, not the display name, so an admin
      // copying values straight out of it needs the id to resolve too - try
      // the display name first (the documented/expected format), then fall
      // back to the id, rather than making every id-based row Invalid.
      const salesAdminUser =
        usersByNormalizedName.get(normalize(row.salesAdminUsername)) ??
        usersByNormalizedId.get(normalize(row.salesAdminUsername));
      const parsedAllowAllClients = parseAllowAllClients(row.allowAllClientsRaw);
      // Display/grouping never see `undefined`: an unrecognized value is
      // always Invalid (checked explicitly below), so `false` here only
      // affects the placeholder text on an already-invalid row.
      const allowAllClients = parsedAllowAllClients === true;
      const hasBdmUsername = row.bdmUsername.length > 0;
      const bdmUser = hasBdmUsername
        ? usersByNormalizedName.get(normalize(row.bdmUsername)) ??
          usersByNormalizedId.get(normalize(row.bdmUsername))
        : null;

      const base = {
        key: `${index}-${row.salesAdminUsername}-${row.bdmUsername}`,
        salesAdminUsername: row.salesAdminUsername,
        bdmUsername: hasBdmUsername
          ? row.bdmUsername
          : allowAllClients
          ? "(all clients)"
          : "",
        allowAllClients,
      };

      if (!salesAdminUser) {
        return {
          ...base,
          action: ACTIONS.INVALID,
          detail: `Unknown Sales Admin username '${row.salesAdminUsername}'`,
        };
      }

      // Fails loudly rather than guessing: a typo'd value with a populated
      // BdmUsername would otherwise import silently as an ordinary
      // single-BDM mapping, dropping the admin's intended allow-all with no
      // warning.
      if (parsedAllowAllClients === undefined) {
        return {
          ...base,
          salesAdminUserId: salesAdminUser.userId,
          action: ACTIONS.INVALID,
          detail: `Unrecognized AllowAllClients value '${row.allowAllClientsRaw}' — use 1/0, true/false, or yes/no.`,
        };
      }

      // A blank BdmUsername is only valid when AllowAllClients is truthy -
      // that is the supported allow-all row shape, and the backend creates a
      // single null-BDM mapping row for it.
      if (!hasBdmUsername && !allowAllClients) {
        return {
          ...base,
          salesAdminUserId: salesAdminUser.userId,
          action: ACTIONS.INVALID,
          detail: "BdmUsername is required unless AllowAllClients is set",
        };
      }

      if (hasBdmUsername && !bdmUser) {
        return {
          ...base,
          salesAdminUserId: salesAdminUser.userId,
          action: ACTIONS.INVALID,
          detail: `Unknown BDM username '${row.bdmUsername}'`,
        };
      }

      const saNorm = normalize(salesAdminUser.userId);
      const bdmNorm = bdmUser ? normalize(bdmUser.userId) : "";
      const existing = mappingsByPair.get(`${saNorm}||${bdmNorm}`);

      let action;
      let detail;

      if (existing) {
        action = existing.active ? ACTIONS.ALREADY_MAPPED : ACTIONS.REACTIVATE;
      } else if (
        bdmNorm &&
        activeSalesAdminByBdm.has(bdmNorm) &&
        activeSalesAdminByBdm.get(bdmNorm) !== saNorm
      ) {
        action = ACTIONS.MAPPED_ELSEWHERE;
        const otherSalesAdminId = activeSalesAdminByBdm.get(bdmNorm);
        const otherUser = usersByNormalizedId.get(otherSalesAdminId);
        detail = `Already mapped to ${otherUser?.userName ?? otherSalesAdminId}`;
      } else {
        action = ACTIONS.ADD;
      }

      return {
        ...base,
        salesAdminUserId: salesAdminUser.userId,
        bdmUserId: bdmUser ? bdmUser.userId : null,
        action,
        detail,
      };
    });
  }, [fileRows, usersByNormalizedName, usersByNormalizedId, mappingsByPair, activeSalesAdminByBdm]);

  const summary = React.useMemo(
    () => ({
      add: previewRows.filter((row) => row.action === ACTIONS.ADD),
      alreadyMapped: previewRows.filter(
        (row) => row.action === ACTIONS.ALREADY_MAPPED
      ),
      reactivate: previewRows.filter((row) => row.action === ACTIONS.REACTIVATE),
      invalid: previewRows.filter((row) => row.action === ACTIONS.INVALID),
      mappedElsewhere: previewRows.filter(
        (row) => row.action === ACTIONS.MAPPED_ELSEWHERE
      ),
    }),
    [previewRows]
  );

  // Already-mapped and mapped-elsewhere rows would be a no-op (or a repeat of
  // a conflict the server already knows about) if they were the only rows on
  // the file, so they don't count toward "is there anything to apply".
  const canApply =
    !submitting &&
    !parseError &&
    summary.add.length + summary.reactivate.length > 0;

  const onAddFileHandler = (event) => {
    setFiles(event.newState);
    setParseError(null);
    setFileRows([]);
    setResults(null);

    const rawFile = event.newState[0]?.getRawFile?.();
    if (!rawFile) return;

    Papa.parse(rawFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => (header || "").trim(),
      complete: (results) => {
        const headers = results.meta?.fields || [];
        const salesAdminHeader = headers.find(
          (header) => header.toLowerCase() === "salesadminusername"
        );
        const bdmHeader = headers.find(
          (header) => header.toLowerCase() === "bdmusername"
        );
        const allowAllHeader = headers.find(
          (header) => header.toLowerCase() === "allowallclients"
        );

        if (!salesAdminHeader) {
          setParseError(
            "The file is missing a required 'SalesAdminUsername' column header."
          );
          return;
        }

        if (!bdmHeader) {
          setParseError(
            "The file is missing a required 'BdmUsername' column header."
          );
          return;
        }

        const rows = results.data
          .map((row) => ({
            salesAdminUsername: String(row[salesAdminHeader] ?? "").trim(),
            bdmUsername: String(row[bdmHeader] ?? "").trim(),
            allowAllClientsRaw: allowAllHeader ? row[allowAllHeader] : "",
          }))
          .filter((row) => row.salesAdminUsername.length > 0);

        if (rows.length === 0) {
          setParseError("The file does not contain any Sales Admin Usernames.");
          return;
        }

        // Reject a file that assigns one Sales Admin two different
        // AllowAllClients values. "Any active row wins" is the resolver's
        // rule for data already stored; an obviously self-contradictory FILE
        // is a mistake, and applying that rule silently would be the wrong
        // kind of forgiving.
        //
        // Rows with an unrecognized AllowAllClients value are skipped here -
        // they are flagged Invalid individually during classification below,
        // rather than participating in (or falsely triggering) this check.
        const allowAllBySalesAdmin = new Map();
        for (const row of rows) {
          const value = parseAllowAllClients(row.allowAllClientsRaw);
          if (value === undefined) continue;

          const key = normalize(row.salesAdminUsername);
          if (!allowAllBySalesAdmin.has(key)) {
            allowAllBySalesAdmin.set(key, value);
          } else if (allowAllBySalesAdmin.get(key) !== value) {
            setParseError(
              `The file has conflicting AllowAllClients values for Sales Admin '${row.salesAdminUsername}'.`
            );
            return;
          }
        }

        setFileRows(rows);
      },
      error: () => {
        setParseError("Failed to parse the file. Please check the format.");
      },
    });
  };

  const onRemoveFileHandler = (event) => {
    setFiles(event.newState);
    setFileRows([]);
    setParseError(null);
    setResults(null);
  };

  const onDownloadTemplate = () => {
    const csvData = Papa.unparse([
      { SalesAdminUsername: "", BdmUsername: "", AllowAllClients: "" },
      // Commented example row, left in the downloaded file for reference.
      // SalesAdminUsername/BdmUsername each accept either the display name
      // (e.g. "Sam Admin") or the user id shown in the grid (e.g. "sa1") -
      // useful when copying ids straight out of the grid instead of typing
      // names. This row is not a real data row: its leading "#" makes it an
      // unknown user, so it previews as Invalid and is skipped on Apply.
      {
        SalesAdminUsername: "# Example: Sam Admin (or sa1)",
        BdmUsername: "Bea Dee (or bdm1)",
        AllowAllClients: "0",
      },
    ]);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Sales Admin Mapping Import Template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Groups the non-invalid preview rows by Sales Admin, since the save
  // command is per-Sales-Admin. AllowAllClients is uniform within a group
  // (guaranteed by the parse-time contradiction check above), so any row's
  // value can be used for the whole group.
  const buildGroups = () => {
    const groups = new Map();

    previewRows.forEach((row) => {
      if (row.action === ACTIONS.INVALID) return;

      if (!groups.has(row.salesAdminUserId)) {
        groups.set(row.salesAdminUserId, {
          salesAdminUserId: row.salesAdminUserId,
          salesAdminUsername: row.salesAdminUsername,
          allowAllClients: row.allowAllClients,
          bdmUserIds: new Set(),
        });
      }

      const group = groups.get(row.salesAdminUserId);
      if (row.bdmUserId) {
        group.bdmUserIds.add(row.bdmUserId);
      }
    });

    return Array.from(groups.values());
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const groups = buildGroups();
    const aggregated = {
      addedCount: 0,
      reactivatedCount: 0,
      alreadyMappedCount: 0,
      invalidCount: 0,
      mappedElsewhereCount: 0,
      outcomes: [],
    };

    try {
      for (const group of groups) {
        // eslint-disable-next-line no-await-in-loop
        const response = await saveHandler.mutateAsync({
          salesAdminUserId: group.salesAdminUserId,
          bdmUserIds: Array.from(group.bdmUserIds),
          allowAllClients: group.allowAllClients,
        });
        const data = response?.data ?? {};

        aggregated.addedCount += data.addedCount ?? 0;
        aggregated.reactivatedCount += data.reactivatedCount ?? 0;
        aggregated.alreadyMappedCount += data.alreadyMappedCount ?? 0;
        aggregated.invalidCount += data.invalidCount ?? 0;
        aggregated.mappedElsewhereCount += data.mappedElsewhereCount ?? 0;
        aggregated.outcomes.push(
          ...(data.outcomes ?? []).map((outcome) => ({
            ...outcome,
            salesAdminUsername: group.salesAdminUsername,
          }))
        );
      }

      setSubmitting(false);
      setResults(aggregated);
    } catch {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onSubmit(results);
    onClose();
  };

  const actionCell = (props) => {
    const action = props.dataItem.action;
    const color =
      action === ACTIONS.ADD
        ? "green"
        : action === ACTIONS.REACTIVATE
        ? "#0d6efd"
        : action === ACTIONS.INVALID
        ? "#a94442"
        : action === ACTIONS.MAPPED_ELSEWHERE
        ? "#8a6d3b"
        : "";
    return (
      <td style={{ color, fontWeight: action === ACTIONS.INVALID ? "bold" : "" }}>
        {action}
      </td>
    );
  };

  return (
    <Dialog title={"Import Sales Admin Mappings"} onClose={onClose} width={800}>
      <ErrorBanner handler={saveHandler} onError={() => setSubmitting(false)} />

      {results ? (
        <div data-testid="save-outcomes">
          <p>
            {results.addedCount} added, {results.reactivatedCount} reactivated,{" "}
            {results.alreadyMappedCount} already mapped, {results.invalidCount}{" "}
            invalid, {results.mappedElsewhereCount} mapped elsewhere.
          </p>
          {results.outcomes
            .filter(
              (outcome) =>
                outcome.outcome === "MappedElsewhere" ||
                outcome.outcome === "Invalid"
            )
            .map((outcome, index) => (
              <p key={`${outcome.bdmUserId}-${index}`}>
                <b>
                  {usersByNormalizedId.get(normalize(outcome.bdmUserId))
                    ?.userName ?? outcome.bdmUserId}
                </b>{" "}
                ({outcome.salesAdminUsername}, {outcome.outcome}):{" "}
                {outcome.detail}
              </p>
            ))}
        </div>
      ) : (
        <>
          <p>
            Upload a CSV with <b>SalesAdminUsername</b> and <b>BdmUsername</b>{" "}
            columns, plus an optional <b>AllowAllClients</b> column. Each of
            the first two columns accepts either the display name or the
            user id shown in the grid.
          </p>
          <p>
            Import adds and reactivates mappings. Mappings not listed in the
            file are left alone — remove them from the grid instead.
          </p>
          <p>
            <Button
              themeColor={"info"}
              fillMode="flat"
              onClick={onDownloadTemplate}
            >
              Download Template
            </Button>
          </p>
          <KendoUpload
            autoUpload={false}
            showActionButtons={false}
            files={files}
            multiple={false}
            onAdd={onAddFileHandler}
            onRemove={onRemoveFileHandler}
            restrictions={{ allowedExtensions: [".csv"] }}
          />
          {parseError && (
            <div
              style={{
                backgroundColor: "#f8d7da",
                padding: 10,
                marginTop: 10,
                borderRadius: ".25rem",
                color: "#721c24",
              }}
            >
              {parseError}
            </div>
          )}
          {previewRows.length > 0 && (
            <>
              <div style={{ marginTop: 20 }}>
                <b>{summary.add.length}</b> to add,{" "}
                <b>{summary.reactivate.length}</b> to reactivate,{" "}
                <b>{summary.alreadyMapped.length}</b> already mapped,{" "}
                <b>{summary.mappedElsewhere.length}</b> mapped elsewhere,{" "}
                <b>{summary.invalid.length}</b> invalid.
              </div>
              {summary.mappedElsewhere.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff3cd",
                    padding: 10,
                    marginTop: 10,
                    borderRadius: ".25rem",
                    color: "#856404",
                  }}
                >
                  {summary.mappedElsewhere.length} row(s) name a BDM already
                  actively mapped to a different Sales Admin. See the Detail
                  column below; the server will re-confirm on Apply.
                </div>
              )}
              {summary.invalid.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#fff3cd",
                    padding: 10,
                    marginTop: 10,
                    borderRadius: ".25rem",
                    color: "#856404",
                  }}
                >
                  {summary.invalid.length} row(s) are invalid and will be
                  skipped. See the Detail column below for the reason.
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <Grid data={previewRows} style={{ maxHeight: "300px" }}>
                  <Column
                    field="salesAdminUsername"
                    title="Sales Admin"
                    width={160}
                  />
                  <Column field="bdmUsername" title="BDM" width={160} />
                  <Column
                    field="allowAllClients"
                    title="Allow All Clients"
                    width={130}
                    cell={(props) => (
                      <td>{props.dataItem.allowAllClients ? "Yes" : "No"}</td>
                    )}
                  />
                  <Column
                    field="action"
                    title="Action"
                    width={150}
                    cell={actionCell}
                  />
                  <Column field="detail" title="Detail" />
                </Grid>
              </div>
            </>
          )}
        </>
      )}

      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        {results ? (
          <Button onClick={handleDone}>{"Close"}</Button>
        ) : (
          <>
            <Button onClick={onClose}>{"Cancel"}</Button>
            <Button
              type={"submit"}
              themeColor={"success"}
              disabled={!canApply}
              onClick={handleSubmit}
            >
              {"Apply"}
              {submitting && <span className="k-icon k-i-loading"></span>}
            </Button>
          </>
        )}
      </DialogActionsBar>
    </Dialog>
  );
};

export default SalesAdminMappingImportModal;
